import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'

import CheckmarkIcon from '~/components/Icons/svgs/CheckmarkIcon'
import ResponsiveImage from '~/components/Image/ResponsiveImage'
import Spinner from '~/components/Spinner'
import SVGIcon from '~/components/Icons/svgs/SVGIcon'
import { useAppSelector } from '~/redux/hooks'
import { useLazyGetLastCancelledSubscriptionTypeQuery } from '~/redux/apis/payment-api'
import subscriptionAnalytics from '~/analytics/subscription/subscriptionAnalytics'
import { isAuthenticated } from '~/auth/slices/auth.slice'
import ROUTES from '~/shared/constants/routes.const'
import useGetStartedEntryRoute from '~/get-started/hooks/useGetStartedEntryRoute'
import { Link } from '~/components/Link'

import { Button } from '../Button'

export interface ModalContentV1Props {
  onCtaClick?: () => void
  isTrial?: boolean
}

const BULLET_KEYS = ['bullet1', 'bullet2', 'bullet3'] as const

export default function ModalContentV1({
  onCtaClick = () => {},
  isTrial = true,
}: ModalContentV1Props): React.ReactElement {
  const { t } = useTranslation('subscription')
  const router = useRouter()

  const authenticated = useAppSelector(isAuthenticated)
  const entryRoute = useGetStartedEntryRoute()

  const [getLastCancelledSubscriptionType, { isFetching: isCancelledTypeFetching }] =
    useLazyGetLastCancelledSubscriptionTypeQuery()

  const handleDirectToSignup = (): void => {
    onCtaClick?.()
    subscriptionAnalytics.logButtonClick('paywall_modal_direct_to_signup')
    subscriptionAnalytics.logCtaClick('paywall_modal_direct_to_signup')
    router.push(entryRoute)
  }

  const handleDirectToPay = async (): Promise<void> => {
    onCtaClick?.()
    subscriptionAnalytics.logButtonClick('paywall_modal_direct_to_pay')
    subscriptionAnalytics.logCtaClick('paywall_modal_direct_to_pay')

    let destination: string = ROUTES.GET_STARTED_PRICE
    try {
      const data = await getLastCancelledSubscriptionType(undefined, true).unwrap()
      if (data?.lastCancelledSubscriptionType === 'family') {
        destination = ROUTES.FAMILY_SWITCH_PROFILE
      } else if (data?.lastCancelledSubscriptionType === 'individual') {
        destination = ROUTES.SUBSCRIPTION_PAGE
      }
    } catch {
      // network/parse error — fall back to default
    }
    router.push(destination)
  }

  const stateKey = authenticated ? 'loggedIn' : 'loggedOut'

  const footerKey = `paywall.subscriptionModalV1.${stateKey}.footer`

  return (
    <div className="relative w-full max-w-[335px] sm:max-w-[620px]">
      <div className="flex flex-col text-white items-center w-full text-center rounded-[16px] overflow-hidden border border-white py-2 bg-purple-darker px-4 sm:px-6 gap-2 sm:gap-3">
        <div className="w-full flex justify-center shrink-0">
          <ResponsiveImage
            alt=""
            fit="contain"
            height={202}
            imageClassName="object-contain w-full max-w-[115px] sm:max-w-[218px]"
            imageWidths="115, 230"
            srcKey="dashboard/subscription-popup.png"
            width={260}
          />
        </div>
        <h2 className="font-extrabold text-white tracking-tight text-center max-w-full text-5 sm:text-3xl leading-tight sm:px-4">
          {t('whatTakesTwoWeeks')}
        </h2>
        <p className="text-blue-pastel px-2 sm:px-8 text-xs sm:text-md">{t('rightMath')}</p>
        <ul className="w-full flex flex-col gap-1 text-left">
          {BULLET_KEYS.map((key) => (
            <li className="flex items-center gap-2 sm:gap-4" key={key}>
              <span className="shrink-0 flex items-center justify-center">
                <SVGIcon icon={CheckmarkIcon} pathClassName="text-white" size={20} />
              </span>
              <span className="text-xxs sm:text-sm font-bold tracking-wide text-white">
                {t(`paywall.subscriptionModalV1.${stateKey}.${key}`)}
              </span>
            </li>
          ))}
        </ul>
        {isTrial && (
          <p className="text-xxs sm:text-xs font-bold text-white/80 text-center">
            {t('paywall.subscriptionModalV1.loggedOut.trialText')}
          </p>
        )}
        <Button
          className="w-full max-w-65 h-12 !px-1 !mt-0 rounded-full border border-white font-black text-sm sm:text-xl text-white tracking-wide flex items-center justify-center shadow-[6px_8px_5.8px_0px_rgba(0,0,0,0.6)] hover:opacity-95 transition-opacity bg-gradient-purple-violet sm:max-w-85 sm:h-14"
          disabled={isCancelledTypeFetching}
          onClick={authenticated ? handleDirectToPay : handleDirectToSignup}
        >
          {isCancelledTypeFetching ? (
            <Spinner color="white" size="small" />
          ) : (
            t('paywall.subscriptionModalV1.loggedOut.cta')
          )}
        </Button>
        {!authenticated && (
          <Link className="hover:scale-105 duration-300" to={ROUTES.LOGIN}>
            {t('header.login')}
          </Link>
        )}
        <p className="text-xxxs sm:text-xs text-blue-pastel text-center opacity-90 max-w-full sm:max-w-lg">
          {t(footerKey)}
        </p>
      </div>
    </div>
  )
}
